import React, { useCallback } from 'react';
import {
  Row, Col, Select,
  Space,
  DatePicker,
} from 'antd';
import PropTypes from 'prop-types';
import moment from 'moment';

import OnTraccrNumberInput from '../../../../common/inputs/OnTraccrNumberInput';
import TableProjectPreload from './TableProjectPreload';
import TableCostcodePreload from './TableCostcodePreload';
import TableFormPreload from './TableFormPreload';
import TableSubmissionPreload from './TableSubmissionPreload';
import TimeCardPreloadSettings from './TimeCardPreloadSettings';

import OnTraccrCheckbox from '../../../../common/inputs/OnTraccrCheckbox';

const DATE_MOD_OPTIONS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Week' },
];

const STATUS_OPTIONS = [
  { value: 'unsubmitted', label: 'Unsubmitted' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
];

function TablePreloadSettings({
  configProps,
  updateConfig,
  sections = [],
  divisions,
  costcodes,
  costcodesLoading,
  dataType,
  templateId,
  isTimeTrackingGrid,
}) {
  const isTimeEntry = dataType === 'TimeEntry';
  const isTimeTrackingGridPreloadFromTimeEntries = isTimeTrackingGrid
    && configProps?.timeTrackingGridPreloadSource === 'timeEntries';
  const isTimeEntryOrTimeTrackingGridPreloadFromTimeEntries = isTimeEntry
    || isTimeTrackingGridPreloadFromTimeEntries;

  const preloadTimeEntriesFixedDate = configProps?.preloadTimeEntriesFixedDate;

  const dateFieldOptions = sections.reduce((acc, section) => {
    const { fields = [], name: sectionName } = section;
    return acc.concat(
      fields.reduce((fieldAcc, field) => {
        const {
          id: fieldId,
          selectedType,
          configProps: {
            title: fieldTitle,
          } = {},
        } = field;
        if (isTimeTrackingGrid 
          && !isTimeTrackingGridPreloadFromTimeEntries 
          && selectedType !== 'dateTime') {
          return fieldAcc;
        }
        if (selectedType !== 'dateRange' && selectedType !== 'dateTime') return fieldAcc;
        return fieldAcc.concat([{
          label: `${sectionName} - ${fieldTitle}`,
          value: fieldId,
        }]);
      }, []),
    );
  }, []);

  const getMax = () => {
    const {
      preloadDateModifier,
    } = configProps ?? {};

    return preloadDateModifier === 'weeks' ? 1 : 7;
  };

  const updateValue = (key) => (value) => {
    let newVal = value;
    if (key === 'preloadDateCount' && !value) newVal = 1;
    if (key === 'preloadDateModifier' && newVal === 'weeks') {
      updateConfig({
        [key]: newVal,
        preloadDateCount: 1,
      });
    } else {
      updateConfig({ [key]: newVal });
    }
  };

  const onPreloadDateFieldChange = useCallback((value) => {
    const newConfig = { preloadDateField: value };
    if (value) {
      newConfig.preloadTimeEntriesFixedDate = undefined;
    }
    updateConfig(newConfig);
  }, [configProps.preloadTimeEntriesFixedDate, updateConfig]);

  const onPreloadTimeEntriesFixedDateChange = useCallback((selectedDate) => {
    const iso = selectedDate ? selectedDate.format('YYYY-MM-DD') : null;
    updateConfig(
      iso
        ? { preloadTimeEntriesFixedDate: iso, preloadDateField: undefined }
        : { preloadTimeEntriesFixedDate: null },
    );
  }, [updateConfig]);

  return (
    <Space direction="vertical">
      { !!isTimeEntry && (
        <OnTraccrCheckbox
          value={
            configProps.preloadExistingEntries
              && !configProps.shouldSavePresetTableSelections
          }
          onChange={(e) => updateConfig({
            preloadExistingEntries: e,
            preloadDateCount: e ? 1 : null,
            preloadDateModifier: e ? 'days' : null,
          })}
          disabled={configProps.shouldSavePresetTableSelections}
          label="Preload Existing Time Entries"
          hoverNode={(
            <div style={{ width: 300 }}>
              Check this box if you want to preload existing time entries
              based off the filters below
            </div>
          )}
        />
      )}
      {
        configProps.preloadExistingEntries
        && (
          <Space direction="vertical" 
            style={{ paddingLeft: isTimeEntryOrTimeTrackingGridPreloadFromTimeEntries ? 20 : 0 }}
          >
            {!isTimeTrackingGrid && (
              <Row align="middle" gutter={10}>
                <Col>
                  In the last
                </Col>
                <Col style={{ height: 32 }}>
                  <OnTraccrNumberInput
                    min={1}
                    max={getMax()}
                    defaultValue={1}
                    precision={0}
                    value={configProps.preloadDateCount ?? 1}
                    onChange={updateValue('preloadDateCount')}
                    style={{ width: 100 }}
                    disabled={configProps.preloadDateField}
                  />
                </Col>
                <Col>
                  <Select
                    options={DATE_MOD_OPTIONS}
                    style={{ width: 100 }}
                    value={configProps.preloadDateModifier ?? 'days'}
                    onSelect={updateValue('preloadDateModifier')}
                    disabled={configProps.preloadDateField}
                  />
                </Col>
              </Row>
            )}
            {isTimeTrackingGridPreloadFromTimeEntries && (
              <Row align="middle" gutter={10}>
                <Col>
                  Preload date
                </Col>
                <Col>
                  <DatePicker
                    value={
                      preloadTimeEntriesFixedDate
                        ? moment(preloadTimeEntriesFixedDate, 'YYYY-MM-DD')
                        : null
                    }
                    onChange={onPreloadTimeEntriesFixedDateChange}
                    format="MMM D, YYYY"
                    allowClear
                    placeholder="Select date"
                    disabled={!!configProps.preloadDateField}
                  />
                </Col>
              </Row>
            )}
            <Row align="middle" gutter={10}>
              <Col>
                {!isTimeTrackingGrid ? 'Or ' : ''} Date Field
              </Col>
              <Col>
                <Select
                  style={{ width: 500 }}
                  options={dateFieldOptions}
                  optionFilterProp="label"
                  allowClear
                  showSearch
                  placeholder="Select a Date Field"
                  value={configProps.preloadDateField}
                  onChange={onPreloadDateFieldChange}
                  disabled={isTimeTrackingGridPreloadFromTimeEntries &&
                    !!preloadTimeEntriesFixedDate}
                />
              </Col>
            </Row>
            {isTimeEntryOrTimeTrackingGridPreloadFromTimeEntries && (
              <>
                <Row align="middle" gutter={10}>
                  <Col>
                    With Status
                  </Col>
                  <Col>
                    <Select
                      options={STATUS_OPTIONS}
                      style={{ width: 500 }}
                      allowClear
                      mode="multiple"
                      value={configProps.preloadStatuses}
                      onChange={updateValue('preloadStatuses')}
                    />
                  </Col>
                </Row>
                <Row>
                  <TableCostcodePreload
                    configProps={configProps}
                    updateConfig={updateConfig}
                    costcodes={costcodes}
                    costcodesLoading={costcodesLoading}
                    sections={sections}
                    hideTitle
                  />
                </Row>
              </>
            )}
            {!isTimeEntryOrTimeTrackingGridPreloadFromTimeEntries && (
              <>
                <TableFormPreload
                  configProps={configProps}
                  updateConfig={updateConfig}
                  divisions={divisions}
                  sections={sections}
                  templateId={templateId}
                  isTimeTrackingGrid={isTimeTrackingGrid}
                />
                {!isTimeTrackingGrid && (
                  <TimeCardPreloadSettings
                    configProps={configProps}
                    updateConfig={updateConfig}
                    divisions={divisions}
                    sections={sections}
                    templateId={templateId}
                    dataType={dataType}
                  />
                )}
                <TableProjectPreload
                  configProps={configProps}
                  updateConfig={updateConfig}
                  divisions={divisions}
                  sections={sections}
                  hideTitle
                />
                {!isTimeTrackingGrid && (
                  <TableCostcodePreload
                    configProps={configProps}
                    updateConfig={updateConfig}
                    costcodes={costcodes}
                    costcodesLoading={costcodesLoading}
                    sections={sections}
                    hideTitle
                  />
                )}
                <TableSubmissionPreload
                  configProps={configProps}
                  updateConfig={updateConfig}
                  sections={sections}
                  hideTitle
                />
              </>
            )}
          </Space>
        )
      }
    </Space>
  );
}

/* eslint-disable react/forbid-prop-types */
TablePreloadSettings.propTypes = {
  configProps: PropTypes.object.isRequired,
  updateConfig: PropTypes.func.isRequired,
  sections: PropTypes.array,
  dataType: PropTypes.string.isRequired,
  divisions: PropTypes.arrayOf(PropTypes.string).isRequired,
  costcodes: PropTypes.arrayOf(PropTypes.shape({})),
  costcodesLoading: PropTypes.bool,
  templateId: PropTypes.string,
  isTimeTrackingGrid: PropTypes.bool,
};

TablePreloadSettings.defaultProps = {
  sections: [],
  costcodes: [],
  costcodesLoading: false,
  templateId: null,
  isTimeTrackingGrid: false,
};

export default TablePreloadSettings;
